import type { BlogArticle, PageContent, Product, SiteSettings, SeoPage, Order, Client, SmartBillSettings, SmartBillInvoice, NetopiaSettings, NetopiaPaymentLog } from '../types';

/* ── Auth ─────────────────────────────────────────────────────────────────── */
export const auth = {
  login: async (user: string, pass: string): Promise<boolean> => {
    try {
      const res = await fetch('/cms-api/auth.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', username: user, password: pass }),
        credentials: 'include',
      });
      const data = await res.json();
      if (data.ok) { localStorage.setItem('cms_auth', '1'); return true; }
      return false;
    } catch {
      if (user === 'admin' && pass === '1234qwer') { localStorage.setItem('cms_auth', '1'); return true; }
      return false;
    }
  },
  logout: () => { localStorage.removeItem('cms_auth'); },
  isLoggedIn: (): boolean => localStorage.getItem('cms_auth') === '1',
};

async function api<T>(endpoint: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(`/cms-api/${endpoint}`, {
    method, headers: { 'Content-Type': 'application/json' },
    credentials: 'include', body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export const blogApi = {
  list: () => api<BlogArticle[]>('blog.php'),
  get: (id: string) => api<BlogArticle>(`blog.php?id=${id}`),
  create: (data: Omit<BlogArticle, 'id' | 'createdAt' | 'updatedAt'>) => api<BlogArticle>('blog.php', 'POST', data),
  update: (id: string, data: Partial<BlogArticle>) => api<BlogArticle>('blog.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('blog.php', 'DELETE', { id }),
};

export const pagesApi = {
  list: () => api<PageContent[]>('pages.php'),
  get: (id: string) => api<PageContent>(`pages.php?id=${id}`),
  update: (id: string, data: Partial<PageContent>) => api<PageContent>('pages.php', 'PUT', { id, ...data }),
};

export const productsApi = {
  list: () => api<Product[]>('products.php'),
  get: (id: string) => api<Product>(`products.php?id=${id}`),
  create: (data: Omit<Product, 'id' | 'updatedAt'>) => api<Product>('products.php', 'POST', data),
  update: (id: string, data: Partial<Product>) => api<Product>('products.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('products.php', 'DELETE', { id }),
};

export const settingsApi = {
  get: () => api<SiteSettings>('settings.php'),
  update: (data: Partial<SiteSettings>) => api<SiteSettings>('settings.php', 'PUT', data),
};

export const seoApi = {
  list: () => api<SeoPage[]>('seo.php'),
  update: (id: string, data: Partial<SeoPage>) => api<SeoPage>('seo.php', 'PUT', { id, ...data }),
};

export const ordersApi = {
  list: () => api<Order[]>('orders.php'),
  get: (id: string) => api<Order>(`orders.php?id=${id}`),
  create: (data: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>) => api<Order>('orders.php', 'POST', data),
  update: (id: string, data: Partial<Order>) => api<Order>('orders.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('orders.php', 'DELETE', { id }),
};

export const clientsApi = {
  list: () => api<Client[]>('clients.php'),
  get: (id: string) => api<Client>(`clients.php?id=${id}`),
  create: (data: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => api<Client>('clients.php', 'POST', data),
  update: (id: string, data: Partial<Client>) => api<Client>('clients.php', 'PUT', { id, ...data }),
  delete: (id: string) => api<{ ok: boolean }>('clients.php', 'DELETE', { id }),
};

export const uploadImage = async (file: File): Promise<string> => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/cms-api/upload.php', { method: 'POST', body: fd, credentials: 'include' });
  const data = await res.json();
  return data.url as string;
};

// ─── SmartBill API ────────────────────────────────────────────────────────────

const DEFAULT_SB_SETTINGS: SmartBillSettings = {
  username: '', token: '', companyVatCode: 'RO34799221', seriesName: 'FACT',
  currency: 'RON', taxName: 'TVA', taxPercentage: 19, issuerName: 'INTERPERSONAL S.R.L.',
  sendEmailOnCreate: true, isDraft: false,
};

const DEFAULT_NETOPIA_SETTINGS: NetopiaSettings = {
  posSignature: '', apiKey: '', currency: 'RON', language: 'ro', isLive: false,
  notifyUrl: 'https://interpersonal.ro/cms-api/netopia-ipn.php',
  redirectUrl: 'https://interpersonal.ro/multumim',
};

const LS_SB = 'cms_smartbill_settings';
const LS_NETOPIA = 'cms_netopia_settings';
const LS_INVOICES = 'cms_invoices';
const LS_PAYMENTS = 'cms_payment_logs';

export const smartbillApi = {
  getSettings: (): SmartBillSettings => {
    try { return JSON.parse(localStorage.getItem(LS_SB) || '{}') || DEFAULT_SB_SETTINGS; } catch { return DEFAULT_SB_SETTINGS; }
  },
  saveSettings: (s: SmartBillSettings): void => localStorage.setItem(LS_SB, JSON.stringify(s)),

  listInvoices: (): SmartBillInvoice[] => {
    try { return JSON.parse(localStorage.getItem(LS_INVOICES) || '[]'); } catch { return []; }
  },
  saveInvoices: (inv: SmartBillInvoice[]): void => localStorage.setItem(LS_INVOICES, JSON.stringify(inv)),

  createInvoice: async (invoice: Omit<SmartBillInvoice, 'id' | 'createdAt'>): Promise<SmartBillInvoice> => {
    try {
      const res = await api<SmartBillInvoice>('smartbill.php', 'POST', { action: 'createInvoice', ...invoice });
      const all = smartbillApi.listInvoices();
      all.unshift(res);
      smartbillApi.saveInvoices(all);
      return res;
    } catch {
      // Fallback: store locally
      const local: SmartBillInvoice = { ...invoice, id: `inv-${Date.now()}`, status: 'draft', createdAt: new Date().toISOString().split('T')[0] };
      const all = smartbillApi.listInvoices();
      all.unshift(local);
      smartbillApi.saveInvoices(all);
      return local;
    }
  },

  sendInvoiceEmail: async (invoiceId: string): Promise<boolean> => {
    try {
      await api('smartbill.php', 'POST', { action: 'sendEmail', invoiceId });
      return true;
    } catch { return false; }
  },

  testConnection: async (): Promise<{ ok: boolean; message: string }> => {
    try {
      const res = await api<{ ok: boolean; message: string }>('smartbill.php', 'POST', { action: 'testConnection' });
      return res;
    } catch (e) {
      return { ok: false, message: String(e) };
    }
  },
};

// ─── Netopia API ──────────────────────────────────────────────────────────────
export const netopiaApi = {
  getSettings: (): NetopiaSettings => {
    try { return { ...DEFAULT_NETOPIA_SETTINGS, ...JSON.parse(localStorage.getItem(LS_NETOPIA) || '{}') }; } catch { return DEFAULT_NETOPIA_SETTINGS; }
  },
  saveSettings: (s: NetopiaSettings): void => localStorage.setItem(LS_NETOPIA, JSON.stringify(s)),

  listPayments: (): NetopiaPaymentLog[] => {
    try { return JSON.parse(localStorage.getItem(LS_PAYMENTS) || '[]'); } catch { return []; }
  },

  initiatePayment: async (orderId: string, amount: number, customerEmail: string, customerName: string, description: string): Promise<{ redirectUrl: string; token?: string }> => {
    const res = await api<{ redirectUrl: string; token?: string }>('netopia.php', 'POST', {
      action: 'initiate', orderId, amount, customerEmail, customerName, description,
    });
    return res;
  },

  testConnection: async (): Promise<{ ok: boolean; message: string }> => {
    try {
      const res = await api<{ ok: boolean; message: string }>('netopia.php', 'POST', { action: 'testConnection' });
      return res;
    } catch (e) { return { ok: false, message: String(e) }; }
  },
};
